import { ComplaintParty } from './complaint-party';
import { Document } from './document.model';
import { ComplaintConsent } from './complaint-consent.model';
import { _ } from "@icc/shared/helpers";
import { ComplaintPosition } from './complaint-position.model';
import { ComplaintsDocumentType } from '../complaints-document-types';


export interface Complaint extends Document {
    type: 'complaint';

    /**
     * Priorytet APS
     */
    authorizedRetailerPriority?: string;

    /**
     * Priorytet dystrybutora
     */
    distributorPriority?: string;

    /**
     * Priorytet producenta
     */
    manufacturerPriority?: string;

    /**
     * Przypisany do
     */
    assignedTo?: ComplaintUserType | 'none';

    /**
     * Id firmy przypisanej
     */
    assignedCompanyId?: number | null;

    /**
     * Id użytkownika przypisanego
     */
    assignedUserId?: number | null;

    /**
     * Id firmy APS
     */
    authorizedRetailerCompanyId: number | null;

    /**
     * Id użytkownika APS
     */
    authorizedRetailerUserId: number | null;

    /**
     * Nazwa użytkownika APS
     */
    authorizedRetailerUserName: string;

    /**
     * Numer telefonu przypisanej osoby APS
     */
    authorizedRetailerPhoneNumber?: string;

    /**
     * Adres e-mail przypisanej osoby APS
     */
    authorizedRetailerEmail?: string;

    /**
     * Autoryzowany punkt sprzedaży
     */
    authorizedRetailer: ComplaintParty | null;

    /**
     * Id firmy dystrybutora
     */
    distributorCompanyId?: number | null;

    /**
     * Id użytkownika dystrybutora
     */
    distributorUserId?: number | null;

    /**
     * Nazwa użytkownika dystrybutora
     */
    distributorUserName?: string;

    /**
     * Numer telefonu przypisanej osoby dystrybutora
     */
    distributorPhoneNumber?: string;

    /**
     * Adres e-mail przypisanej osoby dystrybutora
     */
    distributorEmail?: string;

    /**
     * Dystrybutor
     */
    distributor?: ComplaintParty;

    /** Poprzedni dystrybutorzy, mający dostęp do reklamacji */
    formerDistributors?: Record<number, number>;

    /**
     * Id firmy producenta
     */
    manufacturerCompanyId?: number | null;

    /**
     * Id użytkownika producenta
     */
    manufacturerUserId?: number | null;

    /**
     * Nazwa użytkownika producenta
     */
    manufacturerUserName?: string;

    /**
     * Numer telefonu przypisanej osoby producenta
     */
    manufacturerPhoneNumber?: string;

    /**
     * Adres e-mail przypisanej osoby producenta
     */
    manufacturerEmail?: string;

    /**
     * Id rynku producenta
     */
    manufacturerMarketId?: number | null;

    /**
     * Producent
     */
    manufacturer?: ComplaintParty;

    /**
     * Nazwa klienta
     */
    clientName: string;

    /**
     * Email klienta
     */
    clientEmail: string;

    /**
     * Telefon klienta
     */
    clientPhoneNumber: string;

    /**
     * Adres instalacji
     */
    installationAddress: string;

    /**
     * Data zakupu
     */
    purchaseDate: Date | null;

    /**
     * Zgody
     */
    consents: ComplaintConsent[];

    /**
     * Różne procesy obsługi reklamacji
     */
    multipleWorkflows: boolean;

    /**
     * Typ dokumentu
     */
    documentTypeId: ComplaintsDocumentType;

    /**
     * Indywidualny numer reklamacji
     */
    documentName?: string | null;
    /**
     * Data zmiany statusu
     */
    statusChangedAt: Date | null;

    /**
     * Data zmiany przypisania
     */
    assignChangedAt: Date | null;

    /**
     * Data zmiany przypisania
     */
    noteAddedAt: Date | null;

    /**
     * Data ostatniej modyfikacji dla przepięcia reklamacji do nowego dystrybutora
     *
     * Data nie uzwzględnia zmian dystrybutora w pozycjach
     */
    modifiedForComplaintTransfer: Date | null;

    statusChangeInactivitySent: boolean;
    assignChangeInactivitySent: boolean;
    noteAddedInactivitySent: boolean;

    statusChangeReminderQueuedAt?: Date | null;
    assignChangeReminderQueuedAt?: Date | null;
    noteAddedReminderQueuedAt?: Date | null;

    statusChangeReminderSentAt?: Date | null;
    assignChangeReminderSentAt?: Date | null;
    noteAddedReminderSentAt?: Date | null;

    /**
     * Niezgodny APS wybrany w reklamacji i wskazany w usterkach
     */
    inconsistentAuthorizedRetailer?: boolean;

    /**
     * APS wyznaczony na podstawie usterek
     */
    authorizedRetailerInheritedFromPositions?: boolean;

    /**
     * Numer zamówienia reklamacyjnego
     */
    complaintOrderNumber?: string;

    /**
     * Unikalne numery zamówień do producenta od dystrybutora
     */
    distributorOrderNumbers?: string[];

    /**
     * Status reklamacji APS
     */
    authorizedRetailerStatusId?: number;

    /**
     * Status reklamacji dystrybutora
     */
    distributorStatusId?: number;

    /**
     * Status reklamacji producenta
     */
    manufacturerStatusId?: number;

    /**
     * Historia zmian statusów
     */
    statusHistory?: number[] | null;

    /**
     * Status reklamacji APS ustawiony automatycznie
     */
    authorizedRetailerStatusAuto?: boolean;

    /**
     * Status reklamacji dystrybutora ustawiony automatycznie
     */
    distributorStatusAuto?: boolean;

    /**
     * Status reklamacji producenta ustawiony automatycznie
     */
    manufacturerStatusAuto?: boolean;
}

export type ComplaintUserType = 'authorizedRetailer' | 'distributor' | 'manufacturer';


export type ComplaintDTO = Omit<Complaint, 'externalIds' | 'documentNumber' | 'groups'  | 'flags' | 'email' | '$dirty' | 'createdServer' | 'author' | 'authorizedRetailer' | 'distributor' | 'manufacturer' | 'consents' | 'formerDistributors' | 'distributorOrderNumbers' | 'statusHistory'> & {
    documentNumber?: string;
    externalIds?: string;
    groups?: string;
    flags?: string;
    email?: string;
    author?: string;
    authorizedRetailer?: string;
    distributor?: string;
    manufacturer?: string;
    consents?: string;
    formerDistributors?: string;
    distributorOrderNumbers?: string;
    statusHistory?: string;

};

export const ComplaintNonReadableProps = [
    'id',
    'uuid',
    'externalSource',
    'partialExternalSource',
    'authorCompanyId',
    'authorUserId',
    'author',
    'type',
    'statusId',
    'statusSystemId',
    'rootId',
    'parentId',
    'version',
    'searchableDocumentNumber',
    'number',
    'documentCustomCode',
    'groups',
    'created',
    'modified',
    'createdServer',
    'modifiedServer',
    'deletedAt',
    'rootCreated',
    'machineId',
    '$dirty',
    'toSendToExternalSystem',
    'email',
    'documentNumber',
    'multipleWorkflows',
    'statusChangedAt',
    'assignChangedAt',
    'noteAddedAt',
    'statusChangeInactivitySent',
    'assignChangeInactivitySent',
    'noteAddedInactivitySent',
    'inconsistentAuthorizedRetailer',
    'authorizedRetailerInheritedFromPositions',
    'modifiedForComplaintTransfer',
    'assignedCompanyId',
    'authorizedRetailerUserId',
    'authorizedRetailerEmail',
    'authorizedRetailerPhoneNumber',
    'distributorUserId',
    'distributorEmail',
    'distributorPhoneNumber',
    'manufacturerUserId',
    'manufacturerEmail',
    'manufacturerPhoneNumber',
    'assignedUserId',
    'distributorOrderNumbers',
    'statusHistory',
    'authorizedRetailerStatusAuto',
    'distributorStatusAuto',
    'manufacturerStatusAuto',
    'statusChangeReminderQueuedAt',
    'assignChangeReminderQueuedAt',
    'noteAddedReminderQueuedAt',
    'statusChangeReminderSentAt',
    'assignChangeReminderSentAt',
    'noteAddedReminderSentAt',
] as const satisfies readonly (keyof Complaint)[];

export const ComplaintReadablePropsNames: Record<Exclude<keyof Complaint, typeof ComplaintNonReadableProps[number]>, string> = {
    flags: _('COMPLAINT|Flagi'),
    externalId: _('COMPLAINT|Zewnętrzne ID'),
    externalIds: _('COMPLAINT|Zewnętrzne ID'),
    assignedTo: _('COMPLAINT|Przypisany do'),
    declaredDeadline: _('COMPLAINT|Data planowanej weryfikacji zgłoszenia'),
    isEmpty: _('COMPLAINT|Bez pozycji'),
    isInvalid: _('COMPLAINT|Niepoprawny'),
    lang: _('COMPLAINT|Język'),
    description: _('COMPLAINT|Adnotacja końcowa'),
    documentCode: _('COMPLAINT|Własny kod oferty / zamówienia'),
    authorizedRetailer: _('COMPLAINT|APS'),
    distributor: _('COMPLAINT|Dystrybutor'),
    manufacturer: _('COMPLAINT|Producent'),
    consents: _('COMPLAINT|Zgody'),
    authorizedRetailerPriority: _('COMPLAINT|Priorytet APS'),
    distributorPriority: _('COMPLAINT|Priorytet dystrybutora'),
    manufacturerPriority: _('COMPLAINT|Priorytet producenta'),
    authorizedRetailerCompanyId: _('COMPLAINT|Firma APS'),
    authorizedRetailerUserName: _('COMPLAINT|Przypisana osoba APS'),
    distributorCompanyId: _('COMPLAINT|Firma dystrybutora'),
    formerDistributors: _('COMPLAINT|Poprzedni dystrybutorzy z pozostawionym dostępem'),
    distributorUserName: _('COMPLAINT|Przypisana osoba dystrybutora'),
    manufacturerCompanyId: _('COMPLAINT|Firma producenta'),
    manufacturerUserName: _('COMPLAINT|Przypisana osoba producenta'),
    manufacturerMarketId: _('COMPLAINT|Rynek producenta'),
    clientName: _('COMPLAINT|Imię i nazwisko klienta'),
    clientEmail: _('COMPLAINT|Email klienta'),
    clientPhoneNumber: _('COMPLAINT|Telefon klienta'),
    installationAddress: _('COMPLAINT|Adres instalacji'),
    purchaseDate: _('COMPLAINT|Data zakupu'),
    isDeleted: _('COMPLAINT|Usunięty'),
    isOpened: _('COMPLAINT|Otwarty'),
    documentTypeId: _('COMPLAINT|Typ dokumentu'),
    documentName: _('COMPLAINT|Indywidualny numer reklamacji'),
    complaintOrderNumber: _('COMPLAINT|Numer zamówienia reklamacyjnego'),
    authorizedRetailerStatusId: _('COMPLAINT|Status reklamacji APS'),
    distributorStatusId: _('COMPLAINT|Status reklamacji dystrybutora'),
    manufacturerStatusId: _('COMPLAINT|Status reklamacji producenta'),
};
